import type { AnimationEvent } from '@angular/animations';
import { transition, trigger, useAnimation } from '@angular/animations';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from '@angular/core';
import { fadeOut } from '@freelancer/animations';
import { ButtonComponent } from '@freelancer/ui/button';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { BannerUpsellButtonComponent } from './banner-upsell-button.component';
import { BannerUpsellDisclaimerComponent } from './banner-upsell-disclaimer.component';
import { BannerUpsellPictureComponent } from './banner-upsell-picture.component';
import {
  BannerUpsellColor,
  BannerUpsellPicturePosition,
  BannerUpsellSize,
} from './banner-upsell.types';
import { ColumnComponent } from '../column/column.component';
import { GridComponent } from '../grid/grid.component';

@Component({
  standalone: true,
  selector: 'fl-banner-upsell',
  imports: [ButtonComponent, ColumnComponent, GridComponent, IconModule],
  template: `
    <div
      class="BannerUpsell"
      [attr.data-full-height]="fullHeight"
      [attr.data-picture-position]="picturePosition"
      [attr.data-size]="size"
      [@hideElement]="state"
      (@hideElement.done)="animationDone($event)"
    >
      <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
        <fl-col
          class="BannerUpsell-content tablet:mb-none {{
            bannerUpsellPicture &&
            size !== BannerUpsellSize.SMALL &&
            picturePosition === BannerUpsellPicturePosition.END
              ? 'mb-xsmall'
              : 'mb-none'
          }}"
          [colOrder]="
            picturePosition === BannerUpsellPicturePosition.START
              ? 2
              : undefined
          "
          [col]="size === BannerUpsellSize.SMALL_WITH_IMAGE ? 8 : 12"
          [colTablet]="size === BannerUpsellSize.SMALL ? 10 : 8"
          [colDesktopSmall]="
            size === BannerUpsellSize.XLARGE
              ? 6
              : size === BannerUpsellSize.SMALL
                ? 10
                : 8
          "
        >
          <ng-content select="fl-banner-upsell-background-image"></ng-content>
          @if (
            size !== BannerUpsellSize.SMALL &&
            size !== BannerUpsellSize.SMALL_WITH_IMAGE
          ) {
            <h2
              class="text-body-20 mb-xxsmall"
              [class.text-dark]="color === BannerUpsellColor.DARK"
              [class.text-light]="!(color === BannerUpsellColor.DARK)"
            >
              <ng-content select="fl-banner-upsell-title"></ng-content>
            </h2>
          }
          <div class="TextDark">
            @if (
              size === BannerUpsellSize.SMALL ||
              size === BannerUpsellSize.SMALL_WITH_IMAGE
            ) {
              <p
                class="text-body-16 font-bold mb-none"
                [class.text-color-inherit]="color === BannerUpsellColor.DARK"
                [class.text-light]="!(color === BannerUpsellColor.DARK)"
              >
                <ng-content select="fl-banner-upsell-small-title"></ng-content>
              </p>
            }
          </div>
          <div class="TextDark">
            <div
              class="font-normal  {{
                size === BannerUpsellSize.SMALL ||
                size === BannerUpsellSize.SMALL_WITH_IMAGE
                  ? 'text-body-16'
                  : 'text-body-16'
              }} {{
                bannerUpsellButton || bannerUpsellDisclaimer
                  ? 'mb-xsmall'
                  : 'mb-none'
              }}"
              [class.text-color-inherit]="color === BannerUpsellColor.DARK"
              [class.text-light]="!(color === BannerUpsellColor.DARK)"
            >
              <ng-content select="fl-banner-upsell-description"></ng-content>
            </div>
            <div
              class="font-normal {{
                size === BannerUpsellSize.SMALL
                  ? 'text-body-12'
                  : 'text-body-16'
              }}"
              [class.text-color-inherit]="color === BannerUpsellColor.DARK"
              [class.text-light]="!(color === BannerUpsellColor.DARK)"
            >
              <ng-content select="fl-banner-upsell-disclaimer"></ng-content>
            </div>
          </div>
          <ng-content select="fl-banner-upsell-button"></ng-content>
        </fl-col>
        @if (bannerUpsellPicture) {
          <fl-col
            class="BannerUpsell-picture tablet:mb-none"
            [class.mb-xsmall]="
              picturePosition === BannerUpsellPicturePosition.START
            "
            [col]="size === BannerUpsellSize.SMALL_WITH_IMAGE ? 4 : 12"
            [colTablet]="size === BannerUpsellSize.SMALL ? 2 : 4"
            [colDesktopSmall]="
              size === BannerUpsellSize.XLARGE
                ? 6
                : size === BannerUpsellSize.SMALL
                  ? 2
                  : 4
            "
            [class.mobile:hide]="size === BannerUpsellSize.SMALL"
          >
            <ng-content select="fl-banner-upsell-picture"></ng-content>
          </fl-col>
        }
      </fl-grid>
      @if (closeable) {
        <fl-button
          class="BannerUpsell-closeButton"
          (click)="handleCloseEvent($event)"
        >
          <fl-icon
            i18n-label="Banner upsell close icon label"
            label="Close banner upsell"
            [name]="'ui-close'"
            [color]="_closeButtonColor"
            [size]="IconSize.LARGE"
            [useIconFont]="true"
          >
          </fl-icon>
        </fl-button>
      }
    </div>
  `,
  styleUrls: ['./banner-upsell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('hideElement', [
      transition('visible => hidden', [
        useAnimation(fadeOut, {
          params: { duration: '500' },
        }),
      ]),
    ]),
  ],
})
export class BannerUpsellComponent implements OnInit {
  IconSize = IconSize;
  uiConfig = inject<UiConfig>(UI_CONFIG);

  BannerUpsellColor = BannerUpsellColor;
  BannerUpsellPicturePosition = BannerUpsellPicturePosition;
  BannerUpsellSize = BannerUpsellSize;
  VerticalAlignment = VerticalAlignment;

  state = 'visible';

  @HostBinding('class.IsHidden') bannerHidden = false;

  @Input() closeable = true;
  @Input() closeButtonColor: IconColor;
  @Input() color: BannerUpsellColor = BannerUpsellColor.LIGHT;
  @Input() fullHeight = false;
  @Input() picturePosition: BannerUpsellPicturePosition =
    BannerUpsellPicturePosition.END;
  @Input() size: BannerUpsellSize = BannerUpsellSize.LARGE;

  @Output() closeEvent = new EventEmitter<void>();

  @ContentChild(BannerUpsellPictureComponent)
  bannerUpsellPicture: BannerUpsellPictureComponent;
  @ContentChild(BannerUpsellButtonComponent)
  bannerUpsellButton: BannerUpsellButtonComponent;
  @ContentChild(BannerUpsellDisclaimerComponent)
  bannerUpsellDisclaimer: BannerUpsellDisclaimerComponent;

  _closeButtonColor: IconColor;

  ngOnInit(): void {
    if (this.closeButtonColor) {
      // If a close button color is given, overwrite the default color.
      this._closeButtonColor = this.closeButtonColor;
    } else {
      this._closeButtonColor =
        this.color === BannerUpsellColor.DARK
          ? IconColor.FOREGROUND
          : IconColor.LIGHT;
    }
  }

  handleCloseEvent(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.state = 'hidden';
  }

  animationDone(event: AnimationEvent): void {
    if (event.toState === 'hidden') {
      this.bannerHidden = true;
      this.closeEvent.emit();
    }
  }
}
